import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import beerLogo from './images/logos/tbslogo.webp';
import backgroundImage from './images/backgrounds/bottles.webp';
import ForgotPassword from './fragments/ForgotPassword';
import InfoPopup from './fragments/InfoPopup';
import successIcon from './images/assets/success.svg';

function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [message, setMessage] = useState('');
    const [isLogin, setIsLogin] = useState(true);
    const { loggedIn, setLoggedIn, adminUser, setAdminUser, setAdminAuthLevel, setIsFirstTime, setLoggedUser } = useContext(AuthContext);
    const [forgotVisible, setForgotVisible] = useState(false);
    const [infoPopupVisible, setInfoPopupVisible] = useState(false);
    const [popupTitle, setPopupTitle] = useState(null);
    const [popupMessage, setPopupMessage] = useState(null);
    const [popupIcon, setPopupIcon] = useState(null);

    useEffect(() => {
        // Check for SSO token from Hybris
        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        if (token) {
            // Handle SSO token
            handleSSOLogin(token);
        } else {
            // Check session for existing login
            checkSession();
        }
    }, [location]);

    const checkSession = async () => {
        try {
            const { data } = await axios.get('/api/check-session', { withCredentials: true });
            setLoggedIn(data.loggedIn);
            if (data.loggedIn) {
                navigate('/Home');
            }
        } catch (error) {
            console.error('Session check failed:', error);
        }
    };

    const handleSSOLogin = async (token) => {
        try {
            const { data } = await axios.post('/api/auth/verify', { token }, { withCredentials: true });
            if (data.success) {
                // Set session data
                await axios.post('/api/set-session', {
                    userId: data.userId
                }, { withCredentials: true });
                setLoggedIn(true);
                // Check if the user is an admin
                const params = new URLSearchParams({ userId: data.userId });
                const response = await fetch(`/api/checkAdmin?${params}`);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const adminData = await response.json();
                if (adminData.isAdmin) {
                    setAdminUser(adminData.isAdmin);
                    setAdminAuthLevel(adminData.auth_level);
                } else {
                    setAdminUser(false);
                }
                navigate('/Home');
            } else {
                setMessage(data.error || 'SSO login failed');
            }
        } catch (error) {
            setMessage(error.response?.data?.error || 'SSO login failed: Invalid token');
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        const endpoint = isLogin ? '/api/login' : '/api/register';
        try {
            const { data } = await axios.post(endpoint, { username, password }, { withCredentials: true });
            setMessage(data.message);
            if (!data.changed_on) {
                setIsFirstTime(true);
            } else {
                setIsFirstTime(false);
            }
            if (isLogin) {
                setLoggedIn(true);
                setLoggedUser(username);
                const user_id = username;
                const params = new URLSearchParams({ userId: user_id });
                const response = await fetch(`/api/checkAdmin?${params}`);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const adminData = await response.json();
                if (adminData.isAdmin) {
                    setAdminUser(adminData.isAdmin);
                    setAdminAuthLevel(adminData.auth_level);
                }
                navigate('/Home');
            }
        } catch (error) {
            setMessage('Failed: ' + (error.response?.data?.error || error.message));
        }
    };

    const onForgotPassword = () => {
        setForgotVisible(true);
    };

    const closeForgot = (isSuccess) => {
        setForgotVisible(false);
    };

    const openSuccessMessage = () => {
        setForgotVisible(false);
        const title = 'Password Reset Successful';
        const message = (
            <>
                Your password has been reset. You may log in with your new password immediately.
            </>
        );
        setPopupMessage(message);
        setPopupTitle(title);
        setPopupIcon(successIcon);
        setInfoPopupVisible(true);
    };

    const closeSuccessMessage = () => {
        setInfoPopupVisible(false);
    };

    //const toggleForm = () => setIsLogin(!isLogin);

    return (
        <div className='loginPage'>
            <div className='login-form-container'>
                <div className='login-form'>
                    <div className='logoRibbon'>
                        <img src={beerLogo} alt="The Beer Store Logo" />
                    </div>
                    <div className='center'>
                        <h1>The Beer Store Delivers</h1>
                        <p>Log in with your customer number and password</p>
                    </div>
                    {!loggedIn && (
                        <form onSubmit={handleSubmit}>
                            <div>
                                <input
                                    type="text"
                                    id="username"
                                    placeholder='Customer Number'
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    required
                                />
                            </div>
                            <div>
                                <input
                                    type="password"
                                    id="password"
                                    placeholder='Password'
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </div>
                            <button type="submit">{isLogin ? 'LOG IN' : 'Register'}</button>
                        </form>
                    )}
                    <div className='forgot-text'>
                        <a onClick={onForgotPassword}>Forgot Password?</a>
                    </div>
                    <div className={message ? '' : 'hidden-div'}>
                        <p>{message}</p>
                    </div>
                </div>
            </div>
            {forgotVisible && (
                <div className="popup-container">
                    <div className="popup-overlay" />
                    <ForgotPassword onConfirm={openSuccessMessage} onClose={closeForgot} />
                </div>
            )}
            {infoPopupVisible && (
                <div className="popup-container">
                    <div className="popup-overlay" />
                    <div className="popup-content">
                        <InfoPopup onConfirm={closeSuccessMessage} title={popupTitle} message={popupMessage} icon={popupIcon} />
                    </div>
                </div>
            )}
        </div>
    );
}

export default Login;